import clsx from 'clsx';
import React, { FC, forwardRef, HTMLProps } from 'react';
import classes from './card-media.module.scss';

export type CardMediaProps = {
  /**
   * Src for the background image
   */
  src: string;
  alt: string;
} & Omit<HTMLProps<HTMLDivElement>, 'ref'>;

/**
 * Media component leveraging `forwardRef` to provide a styled container
 * with a background image. Typically used to display media content, such
 * as images or backgrounds, within a card or other UI components.
 *
 * The component uses the `src` prop to set the background image of the
 * container, while allowing additional children to be rendered inside the
 * component.
 *
 * Props:
 * - Accepts all standard `CardMediaProps`, in addition to:
 *   - `src`: The URL of the image to be used as the background.
 *   - `children`: Optional, any elements to render within the container.
 *
 * Forwarded Ref:
 * - A ref can be passed to gain access to the underlying `div` element.
 *
 * Remarks:
 * - Additional class names can be passed via the `className` prop.
 * - Inline styles, including background styles, can be customized through
 *   the `style` prop while inheriting `backgroundImage` from the component logic.
 *
 * Usage Considerations:
 * - Primarily designed for embedding media content in custom layouts or UI cards.
 */
export const Media: FC<CardMediaProps> = forwardRef<HTMLDivElement, CardMediaProps>(
  ({ src, children, alt, ...props }, ref) => {
    return (
      <div
        {...props}
        ref={ref}
        className={clsx(classes.root, props.className, 'card-media-component')}
      >
        <img src={src} alt={alt} className={classes.image}/>
        <div className={classes.content}>
          {children}
        </div>
      </div>
    );
  }
);

Media.displayName = 'Card.Media';